import { useEffect, useMemo, useState } from 'react';
import {
  ApiABCResponseCurveDatapoint,
  useABCResponseCurvesQuery,
} from 'src/api/queries';
import { dayjs } from 'src/libs/dayjs';
import { toApiDates } from 'src/utils/date';
import { useFilterContext } from '../../contexts/FilterContext';

export type ResponseCurveState = {
  data: ApiABCResponseCurveDatapoint[];
  isLoading: boolean;
  isError: boolean;
};

const INIT_RESPONSE_CURVE_STATE: Readonly<ResponseCurveState> = {
  data: [],
  isError: false,
  isLoading: false,
};

export const useABCResponseCurveFetch = (enabled: boolean) => {
  const { currentModelVersion, media, timeRange } = useFilterContext();

  // The curves are computed for the selected period, which the backend requires to lie
  // within the model's data, so the period is limited to it
  const period = useMemo(() => {
    if (!currentModelVersion) {
      return undefined;
    }
    const dataStart = dayjs(currentModelVersion.dataStartDate);
    const dataEnd = dayjs(currentModelVersion.dataEndDate);
    const start = timeRange.start.isBefore(dataStart)
      ? dataStart
      : timeRange.start;
    const end = timeRange.end.isAfter(dataEnd) ? dataEnd : timeRange.end;
    return start.isAfter(end) ? undefined : toApiDates({ start, end });
  }, [currentModelVersion, timeRange]);

  const [responseCurve, setResponseCurve] = useState<ResponseCurveState>(
    INIT_RESPONSE_CURVE_STATE,
  );

  const {
    data: responseCurveApiData,
    isLoading: isLoadingResponseCurve,
    isError: isErrorResponseCurve,
  } = useABCResponseCurvesQuery(
    {
      customer: currentModelVersion?.customerName ?? '',
      model_build: currentModelVersion?.buildID ?? '',
      model_id: currentModelVersion?.modelID ?? '',
      ...period,
    },
    enabled && !!period,
  );

  useEffect(() => {
    if (!enabled) {
      setResponseCurve(INIT_RESPONSE_CURVE_STATE);
      return;
    }

    const filtered =
      responseCurveApiData?.filter((r) =>
        media.some((m) => m.selected && m.id === r.channel),
      ) ?? [];

    setResponseCurve({
      data: filtered,
      isLoading: isLoadingResponseCurve,
      isError: isErrorResponseCurve,
    });
  }, [
    responseCurveApiData,
    isErrorResponseCurve,
    isLoadingResponseCurve,
    enabled,
    media,
  ]);

  return { responseCurve };
};
